import type { User } from '@auth0/auth0-spa-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  getAuth0User,
  isAuth0Authenticated,
  loginWithAuth0,
  logoutAuth0,
} from './auth0Client';

interface Auth0State {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: User;
  error?: string;
}

interface Auth0ContextValue extends Auth0State {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Auth0Context = createContext<Auth0ContextValue | undefined>(undefined);

export function Auth0Provider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Auth0State>({
    isAuthenticated: false,
    isLoading: true,
  });

  const refresh = useCallback(async () => {
    try {
      const isAuthenticated = await isAuth0Authenticated();
      const user = isAuthenticated ? await getAuth0User() : undefined;
      setState({ isAuthenticated, user, isLoading: false });
    } catch (error) {
      setState({
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Auth0の状態取得に失敗しました。',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
    const handleAuthChange = () => {
      void refresh();
    };
    window.addEventListener('auth0-auth-change', handleAuthChange);
    return () => window.removeEventListener('auth0-auth-change', handleAuthChange);
  }, [refresh]);

  const login = useCallback(async () => {
    await loginWithAuth0();
  }, []);

  const logout = useCallback(async () => {
    await logoutAuth0();
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      refresh,
    }),
    [state, login, logout, refresh],
  );

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>;
}

export function useAuth0() {
  const context = useContext(Auth0Context);
  if (!context) {
    throw new Error('useAuth0 must be used within Auth0Provider');
  }
  return context;
}
